import {
	type EdgeConfigurationDraft,
	edgeConfigurationDraftSchema,
	edgeSecretFields,
	edgeSourceFieldsSchema,
	edgeSourceTypes,
} from "@sitescene/shared";

export type Integration = EdgeConfigurationDraft["integrations"][number];
export type Source = EdgeConfigurationDraft["sources"][number];
export const sourceLabels: Record<Source["fields"]["source_type"], string> = {
	axis_air_quality: "AXIS air quality",
	axis_scene_events: "AXIS scene events",
	axis_sound_pressure: "AXIS sound pressure",
	homeassistant_contact: "Home Assistant contact",
	homeassistant_event: "Home Assistant event",
	homeassistant_lock: "Home Assistant lock",
	milestone_event: "Milestone event",
	rtsp_camera: "RTSP camera",
};
export const sourceDescriptions: Record<
	Source["fields"]["source_type"],
	string
> = {
	axis_air_quality: "Air quality readings",
	axis_scene_events: "Detections and camera analytics",
	axis_sound_pressure: "Sound pressure summaries",
	homeassistant_contact: "Door and window state changes",
	homeassistant_event: "Doorbell and other entity events",
	homeassistant_lock: "Lock state changes",
	milestone_event: "XProtect site events",
	rtsp_camera: "Video from a camera stream",
};
export const sourceTopics: Partial<
	Record<Source["fields"]["source_type"], string>
> = {
	axis_air_quality: "tnsaxis:AirQualityMonitor/Metadata",
	axis_scene_events: "tnsaxis:CameraApplicationPlatform//.",
	axis_sound_pressure: "tnsaxis:SoundPressureLevel/Summary",
};
export function importSourcesCsv(
	text: string,
	draft: EdgeConfigurationDraft,
): EdgeConfigurationDraft {
	const [header, ...rows] = parseCsv(text);
	if (!header || !rows.length)
		throw new Error("CSV needs a header and at least one source.");
	const allowed = new Set([
		"desiredState",
		...edgeSecretFields.source,
		...Object.keys(edgeSourceFieldsSchema.shape),
	]);
	if (
		new Set(header).size !== header.length ||
		header.some((key) => !allowed.has(key))
	)
		throw new Error("CSV has duplicate or unsupported columns.");
	const numbers = new Set([
		"segment_duration",
		"sensor_port",
		"video_bitrate",
		"video_fps",
	]);
	const booleans = new Set([
		"audio_enabled",
		"skip_upload",
		"tls_verify",
		"video_passthrough",
	]);
	const imported = rows.map((cells, index) => {
		if (cells.length !== header.length)
			throw new Error(
				`Row ${index + 2}: column count does not match the header.`,
			);
		const values = Object.fromEntries(
			header.map((key, i) => [key, cells[i] ?? ""]),
		);
		const type = edgeSourceTypes.find(
			(type) => type === (values.source_type || "rtsp_camera"),
		);
		if (!type) throw new Error(`Row ${index + 2}: unknown source type.`);
		const source = newSource(type);
		const fields: Record<string, unknown> = { ...source.fields };
		for (const [key, value] of Object.entries(values)) {
			if (!value) continue;
			if (key === "desiredState") {
				if (value !== "running" && value !== "stopped")
					throw new Error(
						`Row ${index + 2}: desiredState must be running or stopped.`,
					);
				source.desiredState = value;
			} else if (key === "rtsp_url" || key === "sensor_password")
				source.secrets[key] = { operation: "replace", value };
			else if (numbers.has(key)) fields[key] = Number(value);
			else if (booleans.has(key)) {
				if (!["0", "1", "false", "true"].includes(value))
					throw new Error(`Row ${index + 2}: ${key} must be true or false.`);
				fields[key] = value === "true" || value === "1";
			} else fields[key] = value;
		}
		const result = edgeSourceFieldsSchema.safeParse(fields);
		if (!result.success)
			throw new Error(
				`Row ${index + 2}: ${result.error.issues.map((issue) => issue.path.join(".") + ": " + issue.message).join("; ")}`,
			);
		return { ...source, fields: result.data };
	});
	const result = { ...draft, sources: [...draft.sources, ...imported] };
	const errors = validationMessages(result);
	if (errors.length) throw new Error(errors.join("; "));
	return result;
}
export function newIntegration(
	type: Integration["fields"]["integration_type"] = "homeassistant",
): Integration {
	return {
		fields: {
			host: "",
			integration_type: type,
			name: "",
			onb_port: 554,
			port: type === "milestone" ? 443 : 8123,
			tls_verify: true,
		},
		id: crypto.randomUUID(),
		secrets: {},
	};
}
export function newSource(
	type: Source["fields"]["source_type"] = "rtsp_camera",
): Source {
	return {
		desiredState: "running",
		fields: {
			audio_enabled: false,
			name: "",
			segment_duration: 60,
			segment_format: type === "rtsp_camera" ? "mp4" : "json",
			sensor_id: "",
			sensor_port: type.startsWith("homeassistant") ? 8123 : 443,
			skip_upload: false,
			source_type: type,
			tls_verify: true,
			video_passthrough: false,
		},
		id: crypto.randomUUID(),
		secrets: {},
	};
}
// Quoted fields, embedded commas/newlines, CRLF, and a final empty cell are supported.
export function parseCsv(text: string): string[][] {
	if (new TextEncoder().encode(text).length > 1024 * 1024)
		throw new Error("CSV exceeds 1 MiB.");
	const rows: string[][] = [];
	let row: string[] = [];
	let cell = "";
	let quoted = false;
	let closed = false;
	const input = text.replace(/^\uFEFF/, "");
	for (let i = 0; i < input.length; i++) {
		const char = input[i];
		if (quoted) {
			if (char === '"') {
				if (input[i + 1] === '"') {
					cell += '"';
					i++;
				} else {
					quoted = false;
					closed = true;
				}
			} else cell += char;
		} else if (char === '"' && !cell && !closed) quoted = true;
		else if (char === "," || char === "\n" || char === "\r") {
			row.push(cell);
			cell = "";
			closed = false;
			if (char !== ",") {
				if (char === "\r" && input[i + 1] === "\n") i++;
				if (row.some(Boolean)) rows.push(row);
				row = [];
			}
		} else {
			if (closed || char === '"')
				throw new Error("CSV contains an invalid quoted field.");
			cell += char;
		}
	}
	if (quoted) throw new Error("CSV contains an unclosed quoted field.");
	row.push(cell);
	if (row.some(Boolean)) rows.push(row);
	if (rows.length > 201) throw new Error("CSV supports up to 200 sources.");
	return rows;
}
export function validationMessages(draft: EdgeConfigurationDraft): string[] {
	const result = edgeConfigurationDraftSchema.safeParse(draft);
	return result.success
		? []
		: result.error.issues.map(
				(issue) => `${issue.path.join(".")}: ${issue.message}`,
			);
}
